import { analyticsEvents, trackEvent, trackPageView } from './lib/analytics'
import { ensureModelViewer } from './lib/modelViewerLoader'
import { getSiteUrl } from './lib/siteApi'

const menuBtn = document.querySelector('.menu-btn')
const menu = document.querySelector('#menu')
const year = document.querySelector('#year')
const pageIsFrench = document.documentElement.lang.toLowerCase().startsWith('fr')
const landingLocale = pageIsFrench ? 'fr' : 'en'
const landingPageName = pageIsFrench ? '/fr/' : '/'
const menuOpenLabel = pageIsFrench ? 'Ouvrir le menu' : 'Open menu'
const menuCloseLabel = pageIsFrench ? 'Fermer le menu' : 'Close menu'

trackPageView({ pageName: landingPageName, locale: landingLocale })

const trackLandingEvent = (eventName, payload = {}) => {
  trackEvent(eventName, {
    page_name: landingPageName,
    locale: landingLocale,
    ...payload,
  })
}

if (year) {
  year.textContent = new Date().getFullYear()
}

if (menuBtn && menu) {
  const closeMenu = () => {
    menu.classList.remove('open')
    menuBtn.setAttribute('aria-expanded', 'false')
    menuBtn.setAttribute('aria-label', menuOpenLabel)
    document.body.classList.remove('menu-open')
  }

  const toggleMenu = () => {
    const isOpen = menu.classList.toggle('open')
    menuBtn.setAttribute('aria-expanded', String(isOpen))
    menuBtn.setAttribute('aria-label', isOpen ? menuCloseLabel : menuOpenLabel)
    document.body.classList.toggle('menu-open', isOpen)
  }

  menuBtn.setAttribute('aria-label', menuOpenLabel)

  menuBtn.addEventListener('click', toggleMenu)

  menu.querySelectorAll('a').forEach((link) => {
    link.addEventListener('click', closeMenu)
  })

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeMenu()
    }
  })

  document.addEventListener('click', (event) => {
    const isOpen = menu.classList.contains('open')
    if (!isOpen) return

    const clickedInsideMenu = menu.contains(event.target)
    const clickedToggle = menuBtn.contains(event.target)
    if (!clickedInsideMenu && !clickedToggle) {
      closeMenu()
    }
  })
}

const hasMatchMedia = typeof window.matchMedia === 'function'
const prefersReducedMotion = hasMatchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection
const prefersSaveData = Boolean(connection && connection.saveData)
const lowPowerDevice =
  prefersReducedMotion ||
  prefersSaveData ||
  (typeof navigator.hardwareConcurrency === 'number' && navigator.hardwareConcurrency <= 4)
const clamp = (value, min, max) => Math.min(max, Math.max(min, value))
const lerp = (a, b, t) => a + (b - a) * t
const createBrushedMetalDataUrl = (tone) => {
  const canvas = document.createElement('canvas')
  canvas.width = 384
  canvas.height = 384
  const ctx = canvas.getContext('2d')

  if (!ctx) return ''

  const [tr, tg, tb] = tone
  const baseR = Math.round(lerp(176, tr, 0.09))
  const baseG = Math.round(lerp(182, tg, 0.09))
  const baseB = Math.round(lerp(195, tb, 0.09))

  const hash = (x, y, seed) => {
    const value = Math.sin((x + seed * 31.3) * 12.9898 + (y + seed * 17.7) * 78.233) * 43758.5453
    return value - Math.floor(value)
  }

  const textureData = ctx.createImageData(canvas.width, canvas.height)
  const data = textureData.data
  let pointer = 0

  for (let y = 0; y < canvas.height; y += 1) {
    const rowWaveA = Math.sin(y * 0.03)
    const rowWaveB = Math.sin(y * 0.011 + 1.3)
    const rowDrift = (rowWaveA * 0.7 + rowWaveB * 0.3) * 0.22

    for (let x = 0; x < canvas.width; x += 1) {
      const brushedA = Math.sin(x * 0.17 + y * 0.012) * 0.48
      const brushedB = Math.sin(x * 0.051 - y * 0.006) * 0.28
      const longBand = Math.sin((x + y * 0.34) * 0.008) * 0.18
      const grain = (hash(x, y, 2) - 0.5) * 0.22
      const micro = (hash(x, y, 7) - 0.5) * 0.1

      const intensity = rowDrift + brushedA * 0.24 + brushedB * 0.18 + longBand * 0.2 + grain + micro
      const lift = intensity * 20 + 14

      data[pointer] = clamp(baseR + lift, 0, 255)
      data[pointer + 1] = clamp(baseG + lift * 0.95, 0, 255)
      data[pointer + 2] = clamp(baseB + lift * 0.92, 0, 255)
      data[pointer + 3] = 255
      pointer += 4
    }
  }

  ctx.putImageData(textureData, 0, 0)

  const sweep = ctx.createLinearGradient(0, canvas.height * 0.15, canvas.width, canvas.height * 0.75)
  sweep.addColorStop(0, 'rgba(255,255,255,0.2)')
  sweep.addColorStop(0.45, 'rgba(255,255,255,0.06)')
  sweep.addColorStop(1, 'rgba(0,0,0,0.08)')
  ctx.fillStyle = sweep
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  const vignette = ctx.createRadialGradient(
    canvas.width * 0.48,
    canvas.height * 0.42,
    canvas.width * 0.12,
    canvas.width * 0.5,
    canvas.height * 0.5,
    canvas.width * 0.66,
  )
  vignette.addColorStop(0, 'rgba(255,255,255,0.06)')
  vignette.addColorStop(1, 'rgba(0,0,0,0.12)')
  ctx.fillStyle = vignette
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  return canvas.toDataURL('image/png')
}

const initCadStages = () => {
  const stages = Array.from(document.querySelectorAll('[data-cad-stage]'))
  if (!stages.length) return

  const heroModelId = '00002200_6553d5a5a37549379146953a_step_003'

  const visualConfig = {
    tone: [248, 159, 110],
    scan: '255, 173, 140',
    orbit: { theta: 24, phi: 72, radius: 134, fov: 25 },
    exposure: 1.48,
    metrics: { pricing: 396.92, dfm: 65 },
  }

  const metricUrlCache = new Map()
  const materialTextureCache = new Map()

  const loadModelUrl = async (modelId) => {
    if (metricUrlCache.has(modelId)) {
      return metricUrlCache.get(modelId)
    }

    const endpoints = [
      getSiteUrl(`/api/models/instantquote/${modelId}`),
      getSiteUrl(`/api/models/${modelId}`),
    ]

    for (const endpoint of endpoints) {
      try {
        const response = await fetch(endpoint)
        if (!response.ok) continue

        const blob = await response.blob()
        if (!blob || blob.size === 0) continue

        const objectUrl = URL.createObjectURL(blob)
        metricUrlCache.set(modelId, objectUrl)
        return objectUrl
      } catch (_error) {
        // try next endpoint
      }
    }

    throw new Error('model_unavailable')
  }

  const localeIsFrench = document.documentElement.lang.toLowerCase().startsWith('fr')

  const formatMetricValue = (metricKey, value) => {
    if (metricKey === 'pricing') {
      const fixed = value.toFixed(2)
      return localeIsFrench ? `${fixed.replace('.', ',')} €` : `$${fixed}`
    }

    return `${Math.round(value)}/100`
  }

  stages.forEach((stage) => {
    const heroVisual = stage.closest('.hero-visual')
    const modelViewer = stage.querySelector('.cad-model')

    if (!heroVisual || !modelViewer) return

    const statusChip = heroVisual.querySelector('.cad-status')
    const metrics = {
      pricing: heroVisual.querySelector('[data-metric="pricing"]'),
      dfm: heroVisual.querySelector('[data-metric="dfm"]'),
    }
    const metricValues = {
      pricing: metrics.pricing?.querySelector('strong') || null,
      dfm: metrics.dfm?.querySelector('strong') || null,
    }

    stage.style.setProperty('--tone-rgb', visualConfig.tone.join(', '))
    stage.style.setProperty('--scan-rgb', visualConfig.scan)

    const state = {
      baseOrbit: { ...visualConfig.orbit },
      exposure: visualConfig.exposure,
      cameraTarget: 'auto auto auto',
      orbitX: 0,
      orbitY: 0,
      zoomOffset: 6,
      isModelLoaded: false,
    }

    const displayedMetrics = { ...visualConfig.metrics }

    // Sinusoidal orbit animation (setInterval at ~30fps; model-viewer interpolates to 60fps)
    const ORBIT_SPIN = 20 / 1000    // 20 deg/s horizontal
    const PHI_A1 = 12, PHI_A2 = 6  // vertical bob amplitudes (deg)
    const PHI_W1 = (2 * Math.PI) / 10000
    const PHI_W2 = PHI_W1 * 1.618  // golden-ratio multiple → never repeats
    let orbitT = 0, orbitLastTick = null, orbitTheta = 0, orbitPhiOff = 0, orbitIntervalId = null
    let dragPhiBase = null  // animated phi captured at drag start, null when not dragging

    const writeMetricValues = (values) => {
      Object.entries(metricValues).forEach(([metricKey, element]) => {
        if (!element) return
        element.textContent = formatMetricValue(metricKey, values[metricKey])
      })
    }

    const applyCamera = () => {
      const theta = state.baseOrbit.theta + state.orbitX
      const basePhi = dragPhiBase !== null ? dragPhiBase : state.baseOrbit.phi
      const phi = clamp(basePhi + state.orbitY, 28, 88)
      const radius = clamp(state.baseOrbit.radius + state.zoomOffset, 116, 176)

      modelViewer.cameraTarget = state.cameraTarget
      modelViewer.cameraOrbit = `${theta.toFixed(2)}deg ${phi.toFixed(2)}deg ${radius.toFixed(2)}%`
      modelViewer.fieldOfView = `${state.baseOrbit.fov}deg`
      modelViewer.exposure = String(state.exposure)
    }

    const startOrbitAnim = () => {
      if (orbitIntervalId || lowPowerDevice) return
      orbitTheta = state.baseOrbit.theta
      dragPhiBase = null
      orbitLastTick = null
      orbitIntervalId = setInterval(() => {
        const now = performance.now()
        const dt = orbitLastTick !== null ? Math.min(now - orbitLastTick, 100) : 0
        orbitLastTick = now
        orbitT += dt
        orbitTheta = (orbitTheta + ORBIT_SPIN * dt) % 360
        orbitPhiOff = PHI_A1 * Math.sin(PHI_W1 * orbitT) + PHI_A2 * Math.sin(PHI_W2 * orbitT)
        const phi = clamp(state.baseOrbit.phi + orbitPhiOff, 28, 88)
        const radius = clamp(state.baseOrbit.radius + state.zoomOffset, 116, 176)
        modelViewer.cameraOrbit = `${orbitTheta.toFixed(1)}deg ${phi.toFixed(1)}deg ${radius.toFixed(1)}%`
      }, 33)
    }

    const stopOrbitAnim = () => {
      if (orbitIntervalId) { clearInterval(orbitIntervalId); orbitIntervalId = null }
      orbitLastTick = null
      state.baseOrbit.theta = orbitTheta
      dragPhiBase = clamp(state.baseOrbit.phi + orbitPhiOff, 28, 88)
    }

    const applyMaterialFinish = async () => {
      if (lowPowerDevice || !state.isModelLoaded || !modelViewer?.model) return

      const materials = modelViewer.model.materials || []
      if (!materials.length || typeof modelViewer.createTexture !== 'function') return

      const textureKey = 'premium_brushed_metal_v6'
      let texture = materialTextureCache.get(textureKey)
      if (!texture) {
        const textureDataUrl = createBrushedMetalDataUrl([205, 210, 224])
        if (!textureDataUrl) return

        texture = await modelViewer.createTexture(textureDataUrl)
        materialTextureCache.set(textureKey, texture)
      }

      materials.forEach((material) => {
        const pbr = material.pbrMetallicRoughness
        if (!pbr) return

        const tintStrength = 0.03
        const tint = visualConfig.tone.map((channel) => channel / 255)
        const base = [
          lerp(0.96, tint[0], tintStrength),
          lerp(0.97, tint[1], tintStrength),
          lerp(0.99, tint[2], tintStrength),
          1,
        ]

        pbr.setBaseColorFactor(base)
        pbr.setMetallicFactor(0.88)
        pbr.setRoughnessFactor(0.26)
        if (pbr.baseColorTexture && typeof pbr.baseColorTexture.setTexture === 'function') {
          pbr.baseColorTexture.setTexture(texture)
        } else if (typeof pbr.setBaseColorTexture === 'function') {
          pbr.setBaseColorTexture(texture)
        }

        if (typeof material.setClearcoatFactor === 'function') {
          material.setClearcoatFactor(0.08)
        }
        if (typeof material.setClearcoatRoughnessFactor === 'function') {
          material.setClearcoatRoughnessFactor(0.36)
        }
        if (typeof material.setSpecularFactor === 'function') {
          material.setSpecularFactor(1)
        }
        if (typeof material.setIor === 'function') {
          material.setIor(1.5)
        }
        if (typeof material.setAnisotropyStrength === 'function') {
          material.setAnisotropyStrength(0.18)
        }
        if (typeof material.setAnisotropyRotation === 'function') {
          material.setAnisotropyRotation(0)
        }
      })
    }

    writeMetricValues(displayedMetrics)

    const onModelLoaded = () => {
      state.isModelLoaded = true
      stage.classList.add('model-ready')
      if (typeof modelViewer.updateFraming === 'function') {
        modelViewer.updateFraming()
      }
      if (typeof modelViewer.getBoundingBoxCenter === 'function') {
        const center = modelViewer.getBoundingBoxCenter()
        if (
          center &&
          Number.isFinite(center.x) &&
          Number.isFinite(center.y) &&
          Number.isFinite(center.z)
        ) {
          state.cameraTarget = `${center.x.toFixed(4)}m ${center.y.toFixed(4)}m ${center.z.toFixed(4)}m`
        }
      }
      modelViewer.shadowIntensity = lowPowerDevice ? 0 : 0.18
      if (typeof modelViewer.shadowSoftness !== 'undefined') {
        modelViewer.shadowSoftness = lowPowerDevice ? 0 : 0.95
      }
      modelViewer.environmentImage = lowPowerDevice ? 'neutral' : 'legacy'
      modelViewer.interactionPrompt = 'none'
      modelViewer.exposure = String(state.exposure)
      modelViewer.autoRotate = false
      modelViewer.setAttribute('interpolation-decay', '40')
      applyCamera()
      startOrbitAnim()
      if (!lowPowerDevice) {
        applyMaterialFinish().catch(() => {
          // keep the model visible even if custom texture setup fails
        })
      }
    }

    const onModelError = () => {
      stage.classList.add('model-fallback')
      if (statusChip) {
        statusChip.textContent = localeIsFrench ? 'Prévisualisation statique' : 'Static preview'
      }
    }

    let hasBootstrappedModel = false
    const bootstrapModel = () => {
      if (hasBootstrappedModel) return
      hasBootstrappedModel = true

      ensureModelViewer()
        .then(async () => {
          modelViewer.addEventListener('load', onModelLoaded, { once: true })
          modelViewer.addEventListener('error', onModelError, { once: true })

          const modelUrl = await loadModelUrl(heroModelId)
          modelViewer.src = modelUrl
        })
        .catch(() => {
          onModelError()
        })
    }

    if (typeof IntersectionObserver === 'function') {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return
          observer.disconnect()
          bootstrapModel()
        },
        { threshold: 0.2 },
      )
      observer.observe(stage)
    } else {
      bootstrapModel()
    }

    // ── Hover tilt + click-drag orbit ──────────────────────────────────────
    if (prefersReducedMotion || prefersSaveData) return

    const MAX_TILT = 5        // deg CSS tilt on hover
    const DRAG_SENS = 0.35    // deg of orbit per pixel dragged
    const LERP = 0.08

    const tilt = { tx: 0, ty: 0, cx: 0, cy: 0, raf: null }
    let dragging = false
    let dragStartX = 0, dragStartY = 0
    let dragBaseOrbitX = 0, dragBaseOrbitY = 0

    const flushTilt = () => {
      stage.style.transform =
        `rotateY(${tilt.cx.toFixed(3)}deg) rotateX(${(-tilt.cy).toFixed(3)}deg)`
    }

    const tickTilt = () => {
      const dx = tilt.tx - tilt.cx
      const dy = tilt.ty - tilt.cy
      tilt.cx += dx * LERP
      tilt.cy += dy * LERP
      flushTilt()
      if (Math.abs(dx) > 0.005 || Math.abs(dy) > 0.005) {
        tilt.raf = requestAnimationFrame(tickTilt)
      } else {
        tilt.cx = tilt.tx; tilt.cy = tilt.ty
        flushTilt()
        tilt.raf = null
      }
    }

    heroVisual.addEventListener('mousemove', (e) => {
      if (dragging || !state.isModelLoaded) return
      const r = heroVisual.getBoundingClientRect()
      tilt.tx = (((e.clientX - r.left) / r.width) * 2 - 1) * MAX_TILT
      tilt.ty = (((e.clientY - r.top) / r.height) * 2 - 1) * MAX_TILT
      if (!tilt.raf) tilt.raf = requestAnimationFrame(tickTilt)
    })

    heroVisual.addEventListener('mouseleave', () => {
      if (dragging) return
      tilt.tx = 0; tilt.ty = 0
      if (!tilt.raf) tilt.raf = requestAnimationFrame(tickTilt)
    })

    stage.addEventListener('mousedown', (e) => {
      if (!state.isModelLoaded || e.button !== 0) return
      dragging = true
      dragStartX = e.clientX; dragStartY = e.clientY
      dragBaseOrbitX = state.orbitX; dragBaseOrbitY = state.orbitY
      stopOrbitAnim()
      if (tilt.raf) { cancelAnimationFrame(tilt.raf); tilt.raf = null }
      tilt.tx = 0; tilt.ty = 0; tilt.cx = 0; tilt.cy = 0
      stage.style.transform = ''
      stage.classList.add('dragging')
      e.preventDefault()
    })

    const onDragMove = (e) => {
      if (!dragging) return
      state.orbitX = dragBaseOrbitX + (e.clientX - dragStartX) * DRAG_SENS
      state.orbitY = clamp(dragBaseOrbitY - (e.clientY - dragStartY) * (DRAG_SENS * 0.5), -22, 22)
      applyCamera()
    }

    const onDragEnd = () => {
      if (!dragging) return
      dragging = false
      stage.classList.remove('dragging')
      state.baseOrbit.theta = (state.baseOrbit.theta + state.orbitX) % 360
      state.orbitX = 0
      state.orbitY = 0
      applyCamera()
      startOrbitAnim()
    }

    document.addEventListener('mousemove', onDragMove)
    document.addEventListener('mouseup', onDragEnd)
  })
}

const initTrackedCtas = () => {
  document.querySelectorAll('[data-cta-id]').forEach((element) => {
    element.addEventListener('click', () => {
      trackLandingEvent(analyticsEvents().CTA_CLICK, {
        cta_id: element.dataset.ctaId,
        placement: element.dataset.ctaPlacement || 'landing',
      })
    })
  })
}

const initDemoForms = () => {
  const forms = document.querySelectorAll('[data-demo-form]')
  if (!forms.length) return

  forms.forEach((form) => {
    const events = analyticsEvents()
    const status = form.querySelector('.form-status')
    const locale = form.dataset.locale === 'fr' ? 'fr' : 'en'
    const submitButton = form.querySelector('.btn-submit')
    const defaultButtonLabel = submitButton?.textContent || ''
    const nameField = form.querySelector('[name="name"]')
    const emailField = form.querySelector('[name="email"]')
    const messageField = form.querySelector('[name="message"]')
    const trackedFields = [nameField, emailField, messageField].filter(Boolean)
    let hasStarted = false

    const setStatus = (text, state) => {
      if (!status) return
      status.textContent = text
      if (state) {
        status.dataset.state = state
        return
      }
      delete status.dataset.state
    }

    const markFieldInvalid = (field, isInvalid) => {
      if (!field) return
      field.setAttribute('aria-invalid', String(isInvalid))
    }

    const clearValidationState = () => {
      trackedFields.forEach((field) => markFieldInvalid(field, false))
    }

    const setSubmitting = (isSubmitting) => {
      if (!submitButton) return
      submitButton.disabled = isSubmitting
      submitButton.setAttribute('aria-busy', String(isSubmitting))

      if (!isSubmitting) {
        submitButton.textContent = defaultButtonLabel
        return
      }

      submitButton.textContent = locale === 'fr' ? 'Envoi en cours...' : 'Submitting...'
    }

    form.addEventListener('input', (event) => {
      if (!hasStarted) {
        hasStarted = true
        trackLandingEvent(events.DEMO_FORM_STARTED)
      }

      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        markFieldInvalid(event.target, false)
      }

      if (status?.dataset.state === 'error') {
        setStatus('', '')
      }
    })

    form.addEventListener('submit', async (event) => {
      event.preventDefault()

      const formData = new FormData(form)
      const name = String(formData.get('name') || '').trim()
      const email = String(formData.get('email') || '').trim()
      const company = String(formData.get('company') || '').trim()
      const role = String(formData.get('role') || '').trim()
      const message = String(formData.get('message') || '').trim()
      const website = String(formData.get('website') || '').trim()
      const sourceCampaign = new URLSearchParams(window.location.search).get('utm_campaign') || 'direct'

      clearValidationState()
      setStatus('', '')

      if (!name) {
        markFieldInvalid(nameField, true)
        setStatus(locale === 'fr' ? 'Veuillez indiquer votre nom.' : 'Please provide your name.', 'error')
        trackLandingEvent(events.DEMO_FORM_SUBMISSION_FAILED, { error_code: 'missing_name' })
        return
      }

      if (!email) {
        markFieldInvalid(emailField, true)
        setStatus(
          locale === 'fr' ? 'Veuillez indiquer un email professionnel.' : 'Please provide a work email.',
          'error',
        )
        trackLandingEvent(events.DEMO_FORM_SUBMISSION_FAILED, { error_code: 'missing_email' })
        return
      }

      if (!message) {
        markFieldInvalid(messageField, true)
        setStatus(
          locale === 'fr'
            ? 'Veuillez décrire votre problématique de chiffrage.'
            : 'Please describe the quoting challenge you want to review.',
          'error',
        )
        trackLandingEvent(events.DEMO_FORM_SUBMISSION_FAILED, { error_code: 'missing_message' })
        return
      }

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        markFieldInvalid(emailField, true)
        setStatus(locale === 'fr' ? 'Format email invalide.' : 'Please provide a valid email format.', 'error')
        trackLandingEvent(events.DEMO_FORM_SUBMISSION_FAILED, { error_code: 'invalid_email_format' })
        return
      }

      setSubmitting(true)

      try {
        const response = await fetch('/api/demo-request', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name,
            email,
            company,
            role,
            message,
            website,
            locale,
            sourceCampaign,
            sourcePage: locale === 'fr' ? '/fr/' : '/',
          }),
        })

        if (!response.ok) {
          throw new Error(`status_${response.status}`)
        }

        const result = await response.json()
        if (!result?.success) {
          throw new Error('submission_failed')
        }

        form.reset()
        clearValidationState()

        if (!result?.notified) {
          setStatus(
            locale === 'fr'
              ? 'Demande enregistrée, mais la notification email a échoué. Merci d’écrire à support@foxhurd.com pour que nous revenions vers vous rapidement.'
              : 'Request saved, but the email notification failed. Please email support@foxhurd.com so we can follow up quickly.',
            'error',
          )
          trackLandingEvent(events.DEMO_FORM_SUBMISSION_FAILED, {
            error_code: String(result?.notification_reason || 'notification_failed'),
          })
          return
        }

        setStatus(
          locale === 'fr'
            ? 'Demande reçue. Nous vous contactons rapidement avec les prochaines étapes.'
            : 'Request received. We will contact you shortly with next steps.',
          'success',
        )
        trackLandingEvent(events.DEMO_FORM_SUBMITTED, {
          source_campaign: sourceCampaign,
          has_company: Boolean(company),
          has_role: Boolean(role),
        })
      } catch (error) {
        setStatus(
          locale === 'fr'
            ? 'Échec de l’envoi. Réessayez ou contactez-nous à support@foxhurd.com.'
            : 'Submission failed. Please retry or email support@foxhurd.com.',
          'error',
        )
        trackLandingEvent(events.DEMO_FORM_SUBMISSION_FAILED, {
          error_code: String(error?.message || 'submit_failed'),
        })
      } finally {
        setSubmitting(false)
      }
    })
  })
}

initCadStages()
initTrackedCtas()
initDemoForms()
